import React, { useEffect, useState, useCallback } from 'react';
import { authHeaders } from '../../services/auth';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { ArrowLeft, MessageSquare, Plus, Send, AlertCircle, RefreshCw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'maintenance', label: 'Maintenance' },
  { id: 'security', label: 'Security' },
  { id: 'facility', label: 'Facility' },
  { id: 'billing', label: 'Billing' },
  { id: 'other', label: 'Other' },
];

const PRIORITIES = [
  { id: 'low', label: 'Low', color: 'bg-slate-200 text-slate-700' },
  { id: 'medium', label: 'Medium', color: 'bg-amber-100 text-amber-700' },
  { id: 'high', label: 'High', color: 'bg-red-100 text-red-700' },
];

const STATUS_META = {
  open: { label: 'Open', color: 'bg-blue-100 text-blue-800' },
  in_progress: { label: 'In Progress', color: 'bg-amber-100 text-amber-800' },
  resolved: { label: 'Resolved', color: 'bg-emerald-100 text-emerald-800' },
  closed: { label: 'Closed', color: 'bg-slate-200 text-slate-700' },
};

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const TicketCreateForm = ({ onCreated, onCancel }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('other');
  const [priority, setPriority] = useState('medium');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (title.trim().length < 3) { toast.error('Title is too short'); return; }
    if (description.trim().length < 3) { toast.error('Description is too short'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/helpdesk`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: title.trim(), description: description.trim(), category, priority }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(j.detail || 'Failed to create ticket'); return; }
      toast.success('Ticket created');
      onCreated?.(j.ticket);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="helpdesk-create-form">
      <div>
        <Label className="text-xs">Title</Label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Short summary"
               data-testid="helpdesk-create-title" maxLength={200} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Category</Label>
          <select value={category} onChange={(e) => setCategory(e.target.value)}
                  data-testid="helpdesk-create-category"
                  className="w-full h-9 px-3 text-sm border border-slate-300 rounded-md bg-white">
            {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs">Priority</Label>
          <select value={priority} onChange={(e) => setPriority(e.target.value)}
                  data-testid="helpdesk-create-priority"
                  className="w-full h-9 px-3 text-sm border border-slate-300 rounded-md bg-white">
            {PRIORITIES.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        </div>
      </div>
      <div>
        <Label className="text-xs">Description</Label>
        <Textarea value={description} onChange={(e) => setDescription(e.target.value)}
                  rows={5} maxLength={4000} placeholder="What happened? When? Any details?"
                  data-testid="helpdesk-create-description" />
      </div>
      <div className="flex gap-2 justify-end pt-1">
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button onClick={submit} disabled={submitting} data-testid="helpdesk-create-submit"
                className="bg-gk-teal hover:bg-gk-teal-hover text-white">
          {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
          Create ticket
        </Button>
      </div>
    </div>
  );
};

const TicketDetail = ({ ticket, role, onBack, onChanged }) => {
  const [data, setData] = useState({ ticket, comments: [] });
  const [loading, setLoading] = useState(false);
  const [comment, setComment] = useState('');
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/helpdesk/${ticket.id}`, { headers: authHeaders(), credentials: 'include' });
      if (res.ok) setData(await res.json());
    } finally { setLoading(false); }
  }, [ticket.id]);

  useEffect(() => { load(); }, [load]);

  const t = data.ticket;
  const comments = data.comments || [];

  const setStatus = async (next) => {
    const res = await fetch(`${API}/api/helpdesk/${t.id}/status`, {
      method: 'PATCH', credentials: 'include',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: next }),
    });
    const j = await res.json().catch(() => ({}));
    if (!res.ok) { toast.error(j.detail || 'Failed to update status'); return; }
    toast.success(`Marked ${next.replace('_', ' ')}`);
    await load();
    onChanged?.();
  };

  const postComment = async () => {
    if (comment.trim().length < 1) return;
    setPosting(true);
    try {
      const res = await fetch(`${API}/api/helpdesk/${t.id}/comments`, {
        method: 'POST', credentials: 'include',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ body: comment.trim() }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(j.detail || 'Failed to post comment'); return; }
      setComment('');
      await load();
      onChanged?.();
    } finally { setPosting(false); }
  };

  const meta = STATUS_META[t.status] || STATUS_META.open;
  const priMeta = PRIORITIES.find((p) => p.id === t.priority) || PRIORITIES[1];
  const closed = t.status === 'closed';

  return (
    <div data-testid={`helpdesk-detail-${t.id}`}>
      <button onClick={onBack} className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 mb-3"
              data-testid="helpdesk-detail-back">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to list
      </button>

      <div className="space-y-1 mb-3">
        <div className="flex items-start gap-2 flex-wrap">
          <h3 className="text-base font-bold text-slate-900 flex-1 min-w-0">{t.title}</h3>
          <Badge className={`${meta.color} border-0 text-[10px]`} data-testid={`helpdesk-status-${t.status}`}>{meta.label}</Badge>
          <Badge className={`${priMeta.color} border-0 text-[10px]`}>{priMeta.label}</Badge>
        </div>
        <p className="text-xs text-slate-500">
          #{t.id.slice(0, 8)} · {t.category} · By {t.created_by_name}{t.villa_number ? ` (${t.villa_number})` : ''} · {formatDate(t.created_at)}
        </p>
      </div>

      <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-800 whitespace-pre-wrap mb-3" data-testid="helpdesk-detail-description">
        {t.description}
      </div>

      {role === 'management' && !closed && (
        <div className="flex flex-wrap gap-2 mb-3">
          {t.status === 'open' && (
            <Button size="sm" onClick={() => setStatus('in_progress')} data-testid="helpdesk-action-in-progress"
                    className="bg-amber-600 hover:bg-amber-700 text-white">Start working</Button>
          )}
          {(t.status === 'open' || t.status === 'in_progress') && (
            <Button size="sm" onClick={() => setStatus('resolved')} data-testid="helpdesk-action-resolve"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white">Mark resolved</Button>
          )}
          {t.status === 'resolved' && (
            <Button size="sm" onClick={() => setStatus('closed')} data-testid="helpdesk-action-close"
                    className="bg-slate-700 hover:bg-slate-800 text-white">Close</Button>
          )}
          {t.status === 'resolved' && (
            <Button size="sm" variant="outline" onClick={() => setStatus('open')}>Re-open</Button>
          )}
        </div>
      )}

      {role === 'resident' && t.status === 'resolved' && (
        <div className="flex flex-wrap gap-2 mb-3">
          <Button size="sm" onClick={() => setStatus('closed')} data-testid="helpdesk-action-resident-close"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white">Confirm & close</Button>
          <Button size="sm" variant="outline" onClick={() => setStatus('open')} data-testid="helpdesk-action-resident-reopen">
            Re-open
          </Button>
        </div>
      )}

      <div className="border-t border-slate-200 pt-3">
        <div className="flex items-center gap-2 mb-2">
          <MessageSquare className="w-4 h-4 text-slate-500" />
          <span className="text-xs font-semibold text-slate-700">Comments ({comments.length})</span>
          <button onClick={load} className="ml-auto text-slate-400 hover:text-slate-700" title="Refresh"
                  data-testid="helpdesk-comments-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <div className="space-y-2 max-h-60 overflow-y-auto pr-1" data-testid="helpdesk-comments-list">
          {comments.length === 0 && (
            <div className="text-xs text-slate-400 italic">No comments yet.</div>
          )}
          {comments.map((c) => (
            <div key={c.id} className={`px-3 py-2 rounded ${c.system ? 'bg-slate-100 italic text-slate-600' : c.author_role === 'management' ? 'bg-blue-50' : 'bg-emerald-50'}`}
                 data-testid={`helpdesk-comment-${c.id}`}>
              <div className="text-[11px] text-slate-500">
                {c.system ? 'System' : `${c.author_name} (${c.author_role})`} · {formatDate(c.created_at)}
              </div>
              <div className="text-sm text-slate-800 whitespace-pre-wrap">{c.body}</div>
            </div>
          ))}
        </div>
        {!closed && (
          <div className="mt-3 flex gap-2">
            <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} maxLength={4000}
                      placeholder="Add a comment…" className="flex-1" data-testid="helpdesk-comment-input" />
            <Button onClick={postComment} disabled={posting || !comment.trim()} data-testid="helpdesk-comment-send"
                    className="self-end bg-gk-teal hover:bg-gk-teal-hover text-white">
              {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export const HelpdeskModal = ({ open, onOpenChange, role }) => {
  const [view, setView] = useState('list'); // 'list' | 'create' | 'detail'
  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState({ open: 0, in_progress: 0, resolved: 0, closed: 0 });
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('open');
  const [activeTicket, setActiveTicket] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = statusFilter
        ? `${API}/api/helpdesk?status=${statusFilter}`
        : `${API}/api/helpdesk`;
      const res = await fetch(url, { headers: authHeaders(), credentials: 'include' });
      const j = await res.json().catch(() => ({ tickets: [], counts: {} }));
      if (res.ok) {
        setTickets(j.tickets || []);
        setCounts(j.counts || {});
      } else {
        setTickets([]);
        toast.error(j.detail || 'Failed to load tickets');
      }
    } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { if (open) { load(); setView('list'); setActiveTicket(null); } }, [open, load]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="helpdesk-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-gk-teal" /> Helpdesk
          </DialogTitle>
          <DialogDescription>
            {role === 'resident'
              ? 'Raise a ticket with your community management.'
              : 'Manage tickets raised by residents.'}
          </DialogDescription>
        </DialogHeader>

        {view === 'create' && role === 'resident' && (
          <TicketCreateForm
            onCreated={() => { setView('list'); load(); }}
            onCancel={() => setView('list')}
          />
        )}

        {view === 'detail' && activeTicket && (
          <TicketDetail
            ticket={activeTicket}
            role={role}
            onBack={() => { setView('list'); setActiveTicket(null); load(); }}
            onChanged={load}
          />
        )}

        {view === 'list' && (
          <div data-testid="helpdesk-list">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <div className="flex flex-wrap gap-1">
                {['open', 'in_progress', 'resolved', 'closed', ''].map((s) => (
                  <button
                    key={s || 'all'}
                    onClick={() => setStatusFilter(s)}
                    data-testid={`helpdesk-filter-${s || 'all'}`}
                    className={`text-xs px-2.5 py-1 rounded-full border ${statusFilter === s ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                  >
                    {s ? STATUS_META[s].label : 'All'}{s && counts[s] !== undefined ? ` (${counts[s] || 0})` : ''}
                  </button>
                ))}
              </div>
              {role === 'resident' && (
                <Button size="sm" onClick={() => setView('create')} data-testid="helpdesk-new-button"
                        className="bg-gk-teal hover:bg-gk-teal-hover text-white">
                  <Plus className="w-4 h-4 mr-1" /> New ticket
                </Button>
              )}
            </div>

            {loading ? (
              <div className="text-center py-8 text-sm text-slate-500">Loading…</div>
            ) : tickets.length === 0 ? (
              <div className="text-center py-10 text-sm text-slate-500" data-testid="helpdesk-empty">
                <AlertCircle className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                {role === 'resident' ? 'No tickets yet. Click "New ticket" to raise one.' : 'No tickets in this view.'}
              </div>
            ) : (
              <ul className="space-y-2">
                {tickets.map((t) => {
                  const meta = STATUS_META[t.status] || STATUS_META.open;
                  const pri = PRIORITIES.find((p) => p.id === t.priority) || PRIORITIES[1];
                  return (
                    <li key={t.id}
                        onClick={() => { setActiveTicket(t); setView('detail'); }}
                        className="border border-slate-200 rounded-lg p-3 hover:bg-slate-50 cursor-pointer"
                        data-testid={`helpdesk-row-${t.id}`}>
                      <div className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-slate-900 truncate">{t.title}</span>
                            <Badge className={`${meta.color} border-0 text-[10px] shrink-0`}>{meta.label}</Badge>
                            <Badge className={`${pri.color} border-0 text-[10px] shrink-0`}>{pri.label}</Badge>
                          </div>
                          <div className="text-[11px] text-slate-500 mt-0.5 truncate">
                            {t.category} · {role === 'management' ? `${t.created_by_name}${t.villa_number ? ` (${t.villa_number})` : ''} · ` : ''}{formatDate(t.created_at)}
                          </div>
                        </div>
                        {t.comment_count > 0 && (
                          <span className="text-[11px] text-slate-500 shrink-0 flex items-center gap-1">
                            <MessageSquare className="w-3 h-3" /> {t.comment_count}
                          </span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default HelpdeskModal;
